import * as React from "react";
import { ThemeProvider } from "styled-components";

import Body from "@/components/core-blocks/Body";
import CookieConsent from "@/components/core-blocks/CookieConsent";
import Footer from "@/components/core-blocks/Footer";

import Header from "@/components/core-blocks/Header";
import Main from "@/components/core-blocks/Main";
import Seo, { SEOProps } from "@/components/core-blocks/Seo";
import Flexible, { IFlexible } from "@/src/components/core-blocks/Flexible";
import GlobalStyle from "@/styles/global";
import { IImage, IPageSettings, IPostTypeContext } from "../types";
import defaultTheme from "@/styles/themes/default.theme";
import { LocalizationProvider } from "@/contexts/Localization";
import TagManager from 'react-gtm-module';

const tagManagerArgs = {
  gtmId: 'GTM-KJMNZ3J'
}

import { PageProps, Script } from "gatsby";

import { Helmet } from "react-helmet";

export interface IPageContext extends IPostTypeContext {
  acf: IFlexible;
  seo: SEOProps;
  pageSettings: IPageSettings;
  title: string;
  customFields: {
    heroButtonColor: string;
    noindex: boolean;
  };
  dynamicMenu: {
    list: string;
  };
  menuLinks: {
    links: string;
  };
  featuredImage?: {
    node?: IImage;
  };
  heroButtonColor?: {
    color: string;
  };
  sticky?: {
    content: string;
    title: string;
    color: string;
  }
}

export interface IPageTemplate
  extends PageProps<object, object, { scrollPosition: number }> {
  pageContext: IPageContext;
}

const PageTemplate: React.FC<IPageTemplate> = ({
  pageContext: { seo, acf, featuredImage, ...pageContext },
  location,
}) => {

  React.useEffect(() => {
    if (location?.state?.scrollPosition) {
      window.scrollTo(0, location.state.scrollPosition);
    }
    TagManager.initialize(tagManagerArgs);

  }, []);

  const marginTop = () => {
    if (acf.components[0].label && acf.components[0].label === "MainHero") {
      return {};
    } else {
      return {
        height: "80px",
        backgroundColor: "#224C5D",
        marginBottom: /nyhetsbrev/.test(pageContext.uri) ? "0" : "100px",
      };
    }
  };

  const seoContent = {
    title: pageContext.title,
    imageUri: featuredImage?.node?.sourceUrl,
    lang: pageContext.language.slug,
    description: "",
    author: "",
    meta: []
  };

  return (
    <LocalizationProvider
      language={pageContext.language.slug}
      translations={pageContext.translations}
      uri={pageContext?.uri}
    >
      <ThemeProvider theme={defaultTheme}>
        <GlobalStyle />
        <Seo {...seoContent} />
        <Helmet
          htmlAttributes={{ lang: pageContext.language.slug }}
        >
          <title>{pageContext.title} - Radar</title>
          {pageContext.customFields.noindex && <meta name="robots" content="noindex, nofollow" />}
        </Helmet>
        <Body>
          <Header dynamicMenu={pageContext.dynamicMenu} />
          <Main>
            <div style={marginTop()} />
            <Flexible
              language={pageContext.language.slug}
              sticky={pageContext.sticky}
              heroButtonColor={pageContext.heroButtonColor && pageContext.heroButtonColor.color}
              menuLinks={pageContext.menuLinks.links}
              components={acf.components}
            />
            <CookieConsent />
          </Main>
          <Footer />
          <Script
            type="text/javascript"
            id="hs-script-loader"
            async
            defer
            src="//js-eu1.hs-scripts.com/6464302.js"
          />
        </Body>
      </ThemeProvider>
    </LocalizationProvider>
  );
};

export default PageTemplate;
